import Vue from 'vue'
import App from '@/App.vue'
import vuetify from '@/plugins/vuetify'
import store from '@/vuex/index'
import router from '@/router'
import 'babel-polyfill'
import interceptorsSetup from '@/helpers/interceptors'
import AppError from '@/plugins/error.js'
import AppConfirm from '@/plugins/confirm.js'
import AppAlert from '@/plugins/alert.js'
import AppUpload from '@/plugins/upload.js'
import AppUser from '@/plugins/user.js'
import AppResident from '@/plugins/resident.js'
import AppKiosk from '@/plugins/kiosk.js'
import AppMessage from '@/plugins/message.js'
import AppMedia from '@/plugins/media.js'
import { EventBus } from '@/event-bus.js'

Vue.config.productionTip = false

// loading overlay
import VueLoading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

// vee-validate
import VeeValidate from 'vee-validate'

// vue-clipboard
import VueClipboard from 'vue-clipboard2'

// vue orientation changer
import VueImgOrientationChanger from 'vue-img-orientation-changer'

// auth0 plugin
import { Auth0Plugin, getInstance } from '@/auth'

// phone input
import VueTelInputVuetify from 'vue-tel-input-vuetify/lib'
import 'vuetify/dist/vuetify.min.css'

// language expressions
import { Expressions } from '@/expression.js'
import { LanguageId } from './config'

Vue.use(Auth0Plugin, {
  domain: process.env.VUE_APP_AUTH_DOMAIN,
  clientId: process.env.VUE_APP_AUTH_CLIENT_ID,
  audience: process.env.VUE_APP_AUTH_AUDIENCE,
  onRedirectCallback: (appState) => {
    router.push(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname
    )
  }
})

Vue.component('loading', VueLoading)
Vue.use(VueImgOrientationChanger)
Vue.use(VueClipboard)

const moment = require('moment')
Vue.use(require('vue-moment'), {
  moment
})
Vue.use(AppError)
Vue.use(AppConfirm)
Vue.use(AppAlert)
Vue.use(AppUpload)
Vue.use(AppUser)
Vue.use(AppResident)
Vue.use(AppKiosk)
Vue.use(AppMessage)
Vue.use(AppMedia)
Vue.use(VeeValidate)
Vue.use(VueTelInputVuetify, {
  vuetify,
});

interceptorsSetup()

Vue.config.productionTip = false

Vue.mixin({
  computed: {
    isSmallDevice () {
      return (
        this.$vuetify.breakpoint.name === 'xs'
      )
    },
    isValidSession () {
      return !this.isNullOrUndefined(this.$store.state.profile.id)
    }
  },
  methods: {
    // languageId is optional
    // provided for overriding the profile language
    getExpression (type, name, languageId) {
      const self = this
      const profileLanguageId = self.$store.state.profile.languageId

      const langId = !self.isNullOrUndefined(languageId)
        ? languageId
        : !self.isNullOrUndefined(profileLanguageId)
          ? profileLanguageId
          : LanguageId.English

      const types = Expressions.filter(e => e.Type === type)
      const names = types.length > 0
        ? types[0].Items.filter(i => i.Name === name)
        : []
      const items = names.length > 0
        ? names[0].Items.filter(i => i.LanguageId === langId)
        : []

      const text = items.length > 0
        ? !self.isSmallDevice
          ? items[0].Text
          : items[0].Short !== undefined
            ? items[0].Short
            : items[0].Text
        : null

      return text
    },

    getLanguagedContentType (contentType) {
      var type = contentType ?? ''

      switch (contentType) {
        case 'audio':
          type = this.getExpression('MenuItem', 'Audio')
          break
        case 'image':
          type = this.getExpression('MenuItem', 'Image')
          break
        case 'video':
          type = this.getExpression('MenuItem', 'Video')
          break
      }

      return type.toLowerCase()
    },

    getFormattedSize (bytes, decimals) {
      if (bytes === null || bytes === undefined) return ''
      var SIZES = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      for (var i = 0, r = bytes, b = 1000; r > b; i++) r /= b
      return `${parseFloat(r.toFixed(decimals))} ${SIZES[i]}`
    },
    
    getFormattedDuration (duration) {
      if (duration === null) return ''
      
      var hours = Math.floor(duration / 3600)
      var minutes = Math.floor((duration - (hours * 3600)) / 60)
      var seconds = duration - (hours * 3600) - (minutes * 60)

      if (hours < 10) { hours = "0" + hours }
      if (minutes < 10) { minutes = "0" + minutes }
      if (seconds < 10) { seconds = "0" + seconds }

      return hours + ':' + minutes + ':' + seconds;
    },

    getMediaType (contentType) {
      return contentType?.substring(0, contentType?.lastIndexOf('/'))
    },
    
    getContentTypeAbbr (contentType) {
      return contentType?.split('/')[0]
    },
    
    getDefaultTitle (filename) {
      const maxTitleSize = 100
      const dotIndex = filename?.lastIndexOf('.')
      const title = filename?.substring(0, dotIndex)?.trim()
      const titleLength = title?.length  ?? 0
      return titleLength > maxTitleSize
        ? title.substring(0, maxTitleSize)
        : title
    },

    onlyUnique (value, index, self) {
      return self.indexOf(value) === index;
    },
    
    underDevelopment () {
      this.$alert.show({
        icon: 'warning',
        formColor: 'yellow darken-2',
        title: 'Men At Work',
        text: 'This feature is currently under development...'
      })
    },
    
    logout () {
      this.$store.dispatch('logout')
    },

    handleCriticalError (e) {
      if (!this.isNullOrUndefined(e)) {
        const code = !this.isNullOrUndefined(e.code)
          ? e.code
          : null

        const status = !this.isNullOrUndefined(e.response)
          ? e.response.status
          : null

        if (code === 'login_required' || status === 401) {
          this.logout()
        }
      }
    },
    
    isNullOrUndefined (obj) {
      return obj === undefined || typeof obj === 'undefined' || obj === null
    }
  }
})

new Vue({
  vuetify,
  store,
  router,
  render: h => h(App),
  mounted() {
    document.addEventListener('mouseup', () => {
      EventBus.$emit('mouse-click')
    })
  }
}).$mount('#app')

// get an insdtance of the auth0 plugin
const authInstance = getInstance()

// add an auth0 watcher for "loading"
authInstance.$watch('loading', async loading => {
  const authenticated = store.state.authenticated
  if (!loading && (authInstance.isAuthenticated || authenticated)) {
    authInstance.getTokenSilently().then(token => {
      const accessToken = token
      authInstance.getIdTokenClaims().then(payload => {
        store.dispatch('authenticated', { accessToken: accessToken, payload: payload })
      })
    }).catch(() => {
      // browsers with cross-site tracking prevention will land here
      try {
        // attempt to log the user in again
        authInstance.loginWithRedirect()
      } catch {
        // if slient login failed, force a logout
        store.dispatch('logout')
      }
    })
  }
})